
import { useState, useEffect, Suspense, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Layout } from "@/components/layout/Layout";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowRight, Layers, Film, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchPlaylists } from "@/utils/supabase/dataFetching";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { SEO } from "@/components/seo/SEO";
import { getPageUrl } from "@/utils/seo/urlUtils";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
// Use named import to prevent dynamic import issues
import { PlaylistCard } from "@/components/home/PlaylistCard";

const PlaylistSkeleton = () => (
  <Card className="overflow-hidden">
    <div className="relative">
      <AspectRatio ratio={2/3}>
        <Skeleton className="h-full w-full" />
      </AspectRatio>
    </div>
    <CardHeader>
      <Skeleton className="h-5 w-3/4 mb-2" />
      <Skeleton className="h-4 w-1/2" />
    </CardHeader>
  </Card>
);

const AllPlaylists = () => {
  const { t } = useLanguage();
  
  const playlistsQuery = useQuery({
    queryKey: ['all-playlists'],
    queryFn: async () => {
      console.log("AllPlaylists - Fetching playlists");
      try {
        const playlists = await fetchPlaylists(undefined, 100);
        
        if (!playlists || playlists.length === 0) {
          console.warn("No playlists returned from API");
        }
        
        console.log("AllPlaylists - Received playlists in order:", 
          playlists?.map(p => ({id: p.id, name: p.name, created_at: p.created_at})) || []);
        
        return playlists || [];
      } catch (error) {
        console.error("Error fetching playlists:", error);
        toast.error("Failed to load playlists");
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  const isLoading = playlistsQuery.isLoading;
  const playlists = playlistsQuery.data || [];

  // Transform playlists to match expected format for PlaylistCard
  const formattedPlaylists = playlists.map(playlist => ({
    id: playlist.id,
    name: playlist.name,
    channelName: playlist.channels?.name || '',
    thumbnailUrl: playlist.thumbnail || '',
    count: playlist.views || 0
  }));

  return (
    <Layout>
      <SEO 
        title={t('playlists.allPlaylists')}
        description={t('playlists.description')}
        keywords="recut, video playlists, content collections, themed videos, curated clips"
        ogUrl={getPageUrl("/playlists")}
      />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center">
            <Layers className="h-6 w-6 mr-2" />
            {t('playlists.allPlaylists')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t('playlists.description')}
          </p>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <PlaylistSkeleton key={i} />
            ))}
          </div>
        ) : formattedPlaylists.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t('playlists.noPlaylists')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {formattedPlaylists.map((playlist) => (
              <PlaylistCard key={playlist.id} playlist={playlist} />
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllPlaylists;
